import styled from '@emotion/styled';
import CoreButton from '@shared/components/core/button/CoreButton';
import { DisplayXSmall, LightMedium } from '@shared/components/core/text/Typography';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate, useRouteError } from 'react-router';

const Container = styled.div`
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    gap: 1rem;
    min-height: 100vh;
    padding: 2rem;
    text-align: center;
    background: ${(props) => props.theme.color.background};
`;

const Message = styled(LightMedium)`
    max-width: 32rem;
`;

const Actions = styled.div`
    display: flex;
    flex-wrap: wrap;
    gap: 0.75rem;
    justify-content: center;
    padding-top: 0.5rem;
`;

interface AppErrorBoundaryProps {
    /**
     * Platform-specific error reporting hook (e.g. Sentry on desktop). Called once
     * when the boundary mounts. Kept optional so the boundary stays platform-agnostic.
     */
    onError?: (error: unknown) => void;
}

/**
 * Root-level route error boundary. Replaces react-router's built-in developer error
 * screen (which ships the library's dev build with a raw stack trace) with a friendly,
 * recoverable UI. The underlying error is logged in the background rather than shown.
 */
export default function AppErrorBoundary({ onError }: AppErrorBoundaryProps) {
    const error = useRouteError();
    const { t } = useTranslation();
    const navigate = useNavigate();

    useEffect(() => {
        // Log in the background instead of surfacing raw errors to the user.
        console.error('Route error boundary caught an error:', error);
        onError?.(error);
        // Report only once when the boundary mounts.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <Container role="alert">
            <DisplayXSmall>{t('error:appError.title')}</DisplayXSmall>
            <Message>{t('error:appError.description')}</Message>
            <Actions>
                <CoreButton $color="primary" onClick={() => window.location.reload()}>
                    {t('common:reload')}
                </CoreButton>
                <CoreButton $color="secondary" onClick={() => navigate('/')}>
                    {t('error:appError.home')}
                </CoreButton>
            </Actions>
        </Container>
    );
}
